import Fuse from "fuse.js";

import { FuzzyNameMatchThreshold, matchByName } from "@/src/lib/name-match";
import { Practitioner } from "@/src/models/practitioner";

/**
 * Search for providers (doctors) using exact matching for NPI and fuzzy matching for names.
 * Prioritizes NPI, then exact name match, then uses fuzzy search.
 *
 * @param practitioners - Array of practitioners to search through
 * @param query - Provider object, search string, or null
 * @returns Array of matching practitioners (first match for object queries, up to 20 for string queries)
 */
export function providersSearch(
  practitioners: Practitioner[],
  query: Partial<Practitioner> | string | null
): Practitioner[] {
  if (!practitioners || practitioners.length === 0) {
    return [];
  }

  if (query === null) {
    return [];
  }

  if (typeof query === "string") {
    const searchQuery = query.trim();

    if (!searchQuery) {
      return [];
    }

    // Numeric-only string queries are treated as NPI prefix matches instead of a fuzzy name search
    if (/^\d+$/.test(searchQuery)) {
      // Prefix match, handles the full length case as well
      const partialNpiMatches = practitioners.filter((p) => {
        return p.npiNumber?.startsWith(searchQuery);
      });

      return partialNpiMatches.slice(0, 20);
    }

    const indexedPractitioners = practitioners.map((p) => ({
      ...p,
      searchName: [p.givenName, p.middleName, p.familyName].filter(Boolean).join(" "),
    }));

    const fuse = new Fuse(indexedPractitioners, {
      keys: ["searchName"],
      threshold: 0.45,
      ignoreLocation: true,
      isCaseSensitive: false,
    });

    const results = fuse.search(searchQuery, { limit: 20 }).map((r) => r.item);

    return results;
  }

  // 1. Prioritize search by NPI if it exists (exact match)
  if (query.npiNumber && query.npiNumber !== "") {
    const npiMatches = practitioners.filter((p) => p.npiNumber && p.npiNumber === query.npiNumber);

    if (npiMatches.length > 0) {
      return [npiMatches[0]]; // Return only the first NPI match
    }
  }

  // Check if any name criteria are provided for further search
  const hasNameCriteria = query.givenName || query.familyName;

  if (!hasNameCriteria) {
    return []; // No NPI match and no name criteria to search by
  }

  // 2. Name matching: exact first+last first, then compound-token containment,
  //    then strict fuzzy token matching.
  const matches = matchByName(
    practitioners,
    { givenName: query.givenName, middleName: query.middleName, familyName: query.familyName },
    FuzzyNameMatchThreshold.Strict
  );

  return matches.length > 0 ? [matches[0]] : [];
}
